import React, { useEffect, useRef, useState } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { apiService, ApiError } from '../services/api';
import { ConvexRealtimeProvider } from '../contexts/ConvexRealtimeProvider';
import { Layout } from '../components/layout/Layout';
import { Loader2 } from 'lucide-react';

export function UserPage() {
  const { userId } = useParams<{ userId: string }>();
  const { isAuthenticated, isLoading } = useAuth();
  const [isLoadingUser, setIsLoadingUser] = useState(false);
  const [error, setError] = useState('');
  const hasAttemptedRef = useRef(false);

  const managerSession = localStorage.getItem('managerSession');
  const hasManagerToken =
    !!localStorage.getItem('managerAccessToken') ||
    !!localStorage.getItem('managerRefreshToken');
  const isManager = !!(managerSession && hasManagerToken);

  useEffect(() => {
    if (isLoading || !userId || !isManager) return;

    // If sessionStorage already has this exact user, nothing to do
    const storedRaw = sessionStorage.getItem('user');
    if (storedRaw) {
      try {
        const stored = JSON.parse(storedRaw);
        if (stored.id === userId) return;
      } catch {}
    }

    if (hasAttemptedRef.current) return;
    hasAttemptedRef.current = true;

    const loadUser = async () => {
      setIsLoadingUser(true);
      try {
        const response = await apiService.getAdminUser(userId);
        const userData = response?.data?.user || response?.data || response?.user || response;
        const phoneNumber = userData?.phoneNumber || userData?.phone || userData?.phone_number;

        if (!phoneNumber) {
          setError('User not found or phone number missing.');
          setIsLoadingUser(false);
          return;
        }

        const loginResponse = await apiService.managerLoginAsUser(phoneNumber);
        sessionStorage.setItem('user', JSON.stringify(loginResponse.user));
        window.location.reload();
      } catch (err) {
        setError(err instanceof ApiError ? err.message : 'Failed to load user');
        setIsLoadingUser(false);
      }
    };

    loadUser();
  }, [userId, isLoading, isManager]);

  if (isLoading || isLoadingUser) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex items-center space-x-2">
          <Loader2 className="h-8 w-8 animate-spin" />
          <span className="text-lg">Loading user...</span>
        </div>
      </div>
    );
  }

  if (!isManager) {
    return <Navigate to="/login" replace />;
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center space-y-3">
          <p className="text-sm text-red-600">{error}</p>
          <a href="/admin" className="text-sm text-blue-600 hover:underline">
            Back to Admin
          </a>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <ConvexRealtimeProvider>
      <Layout />
    </ConvexRealtimeProvider>
  );
}
